import { useUserContext } from "@context/UserContext";
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { checkCpfExists, getCurrentUser, searchUsers, zafClient } from "@services/Zendesk";
import { useFormErrors } from "@hooks/useFormErrors";
import { useUserForm } from "@hooks/useUserForm";
import { useUserExistenceCheck } from "@hooks/useUserExistenceCheck";
import type { User } from "types/UserZendesk";
import { useCustomFields, loadUserFieldsOnce } from "@hooks/useNewUserCustomFields";
import { useAccountType } from "@hooks/useAccountType";
import { formatCNPJ, formatPhone } from '@hooks/masks';
import { useValidation } from "@hooks/validation";

export function useEditUser() {
    const { selectedUser, updateUser, userFields, setUserFields, selectedRole } = useUserContext();
    const navigate = useNavigate();
    const { formData, setFormData } = useUserForm(selectedUser);
    const {
        existUser,
        existUserEmail,
        isChecking,
        checkEmailExists,
        setExistUser,
        setExistUserEmail
    } = useUserExistenceCheck(selectedUser);
    const { error, emailError, setError, setEmailError, setCnpjError, cnpjError } = useFormErrors();
    const [typeUser, setTypeUser] = useState<User[] | null>(null);
    const { isValidEmail, isValidCPFStructure, isValidCNPJStructure } = useValidation();
    const hasLoadedRef = useRef(false);

    const {
        customFields,
        handleCustomFieldChange,
        getVisibleFields,
        typeAccount,
        tipoContas,
        setCustomFields,
    } = useCustomFields(selectedUser?.user_fields);

    const tipoContaValue = tipoContas ||
        (selectedRole === 'revenda' || selectedRole === 'posto' ? 'pessoa_juridica' : null);

    useEffect(() => {
        if (tipoContaValue && tipoContaValue !== tipoContas) {
            setCustomFields(prev => ({ ...prev, tipo_de_conta: tipoContaValue }));
        }
    }, [tipoContaValue, tipoContas]);


    const { isLegalEntity } = useAccountType(selectedUser?.role || '');
    const [userAlcada, setUserAlcada] = useState<string | null>(selectedUser?.user_fields.alcada_do_usuario || null);

    useEffect(() => {
        if (selectedUser?.user_fields) {
            setCustomFields(selectedUser.user_fields);
        }
    }, [selectedUser, setCustomFields]);

    useEffect(() => {
        if (hasLoadedRef.current) return;
        hasLoadedRef.current = true;

        const fetchUserFields = async () => {
            try {
                const user = await getCurrentUser(zafClient);
                const alcada = await searchUsers(zafClient, `email:${user.email}`);
                setTypeUser(alcada);

                if (!userFields || userFields.length === 0) {
                    const fields = await loadUserFieldsOnce();
                    setUserFields(fields);
                }
            } catch (error) {
                console.error('Failed to fetch user fields:', error);
            }
        };
        fetchUserFields();
    }, []);

    const handleInputChange = async (
        field: keyof typeof formData,
        value: string
    ) => {
        if (field === 'cpf_3') {
            const rawValue = value.replace(/\D/g, '')
            setError(null);
            const cleanValue = rawValue.replace(/\D/g, '');

            if (cleanValue.length === 11) {
                if (!isValidCPFStructure(rawValue)) {
                    setError('CPF com 11 dígitos repetidos inválido');
                    return value;
                }
            }
            setFormData(prev => ({ ...prev, [field]: value }));

            const delayDebounce = setTimeout(async () => {
                const cleanForCheck = rawValue.replace(/\D/g, '');
                if (cleanForCheck.length === 11) {
                    const { exists, user } = await checkCpfExists(zafClient, rawValue);
                    setError(exists ? 'Já existe um usuário com este CPF' : null);
                    setExistUser(exists ? user as unknown as User : null);
                }
            }, 500);
            return () => clearTimeout(delayDebounce);
        } else if (field === 'email') {
            setEmailError(null);

            setFormData(prev => ({ ...prev, [field]: value }));
            if (selectedUser?.email && value && !isValidEmail(value)) {
                setEmailError('Email inválido');
                return value;
            }
            const delayDebounce = setTimeout(async () => {
                if (value && value !== selectedUser?.email) {
                    const { exists } = await checkEmailExists(value);
                    if (exists) {
                        setEmailError('Já existe um usuário com este e-mail');
                    } else {
                        setEmailError(null);
                        setExistUserEmail(null);
                        setExistUser(null);
                        setError(null);
                    }
                }
            }, 500);
            return () => clearTimeout(delayDebounce);
        } else if (field === 'phone') {
            const formattedValue = formatPhone(value);
            setFormData(prev => ({ ...prev, [field]: formattedValue }));
        } else if (field === 'cnpj_regex') {
            setCnpjError(null);
            const formattedValue = formatCNPJ(value);

            if (formattedValue.length === 14 || formattedValue.length === 18) {
                if (!isValidCNPJStructure(formattedValue)) {
                    setCnpjError('CNPJ com 14 dígitos inválido');
                    return;
                }
            }
            setFormData(prev => ({ ...prev, [field]: formattedValue }));
        }
        else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    function shouldDisableField(
        fieldKey: string,
        originalValue: string | null | undefined,
        userRole: string
    ) {
        const isCpfOrCnpj = fieldKey === "cpf_3" || fieldKey === "cnpj_regex";

        if (userRole === "admin" || userRole === "agent") return false;

        if (!isCpfOrCnpj) return false;

        if (originalValue) return true;

        return false;
    }

    async function handleProceed() {
        if (!selectedUser) return;

        const cleanCpf = formData?.cpf_3?.replace(/\D/g, '') || '';
        const cleanCnpj = formData?.cnpj_regex?.replace(/\D/g, '') || '';

        const isCpf = cleanCpf.length === 11;
        const isCnpj = cleanCnpj.length === 14 || cleanCnpj.length === 18;

        const razaoSocial = customFields['[Geral] Razão social'] || customFields['_pessoa_juridica__razao_social'] || "";
        const isLegalEntity = tipoContaValue === 'pessoa_juridica';
        const userName = isLegalEntity && razaoSocial ? razaoSocial : formData.name;


        const oldEmail = selectedUser.email;
        const currentSecondaryEmails = selectedUser.secondary_emails || [];
        let secondaryEmails = currentSecondaryEmails;

        if (oldEmail && !oldEmail.includes('@email.com')) {
            if (!currentSecondaryEmails.includes(oldEmail)) {
                secondaryEmails = [...currentSecondaryEmails, oldEmail];
            }
        }
        updateUser({
            ...selectedUser,
            name: userName,
            phone: formData.phone,
            email: formData.email,
            secondary_emails: secondaryEmails,
            user_fields: {
                ...selectedUser.user_fields,
                ...customFields,
                cpf_3: isCpf ? cleanCpf : " ",
                cnpj_regex: isCnpj ? cleanCnpj : " ",
                alcada_do_usuario: userAlcada,
                tipo_de_conta: tipoContaValue,
                _pessoa_juridica__razao_social: isLegalEntity ? (razaoSocial || null) : null,
            }
        });

        navigate("/review-user");
    }

    const visibleFields = useMemo(
        () => getVisibleFields(selectedUser && selectedUser?.role.toLowerCase(), isLegalEntity),
        [userFields, selectedUser?.role, isLegalEntity, getVisibleFields]
    );

    const isCpfComplete = formData?.cpf_3?.replace(/\D/g, '').length === 11;
    const name = formData.name || customFields['[Geral] Cognome'] || customFields['[Geral] Razão social'] || formData?.email?.split('@')[0];
    const isRemovingEmail = selectedUser?.email && !formData.email?.trim();
    const emailRemovalError = isRemovingEmail ? 'O e-mail não pode ser removido, apenas alterado ou mantido' : null;
    const isButtonDisabled =
        !!error ||
        !!emailError ||
        !!existUser ||
        isRemovingEmail ||
        !!existUserEmail ||
        ((selectedUser?.role === 'juiz' || selectedUser?.role === 'advogado') &&
            (formData?.cpf_3?.replace(/\D/g, '') || '') !== (selectedUser?.user_fields?.cpf_3?.toString() || '') &&
            !isCpfComplete) ||
        ((selectedUser?.role === 'transportadora' ||
            selectedUser?.role === 'posto' ||
            typeAccount === 'pessoa_juridica') &&
            formData.cnpj_regex?.replace(/\D/g, '').length > 0 &&
            formData.cnpj_regex?.replace(/\D/g, '').length < 14) ||
        (typeAccount === 'pessoa_fisica' &&
            (formData?.cpf_3?.replace(/\D/g, '') || '') !== (selectedUser?.user_fields?.cpf_3?.toString() || '') &&
            !isCpfComplete) ||
        (typeAccount === 'pessoa_fisica' && !name);
    const account = {
        searchTerm: '',
        setSearchTerm: () => { },
        users: [],
        loading: false,
        newUser: formData,
        setNewUser: setFormData,
        searchUsers: () => { },
        prepareNewUser: handleProceed,
        selectedRole: selectedRole || selectedUser?.role || '',
        selectExistingUser: () => { },
        cpfError: error,
        handleInputChange,
        navigate,
        existUser,
        emailError,
        existUserEmail,
        visibleFields,
        customFields,
        handleCustomFieldChange,
        isLegalEntity,
        setUserAlcada,
        isCpfComplete,
        noUsers: '',
        tipoContas: tipoContaValue,
        isButtonDisabled,
        emailRemovalError,
    };

    return {
        formData,
        handleInputChange,
        handleProceed,
        selectedUser,
        navigate,
        error,
        emailError,
        isChecking,
        existUser,
        existUserEmail,
        customFields,
        handleCustomFieldChange,
        userFields,
        visibleFields,
        typeUser,
        account,
        isButtonDisabled,
        cnpjError,
        shouldDisableField
    };
}
